import dayjs from "dayjs";
import reduxStore from "../redux";
import { addReport, incrementCall } from "../redux/action/report";
import {
  fetchAnalysisDay,
  fetchAnalysisToday,
  fetchHourlyMultiple,
  fetchStoreAnalytics,
  fetchUnitsDay,
} from "./api";
import {
  DATE_FORMAT,
  HOURLY_BATCH_SIZE,
  MAX_SYNCED_UNIT,
  NO_PROPERTY_SITE,
  REPORT_TYPES,
  STORE_ANALYTICS_CONCURRENCY,
  TIME_FORMAT,
} from "./constants";

//------------------------------------ Shared helpers ------------------------------------

// Every date from start to end, inclusive, each at local midnight.
export const findDatesInRange = (start, end) => {
  const dates = [];
  let day = dayjs(start).startOf("day");
  const last = dayjs(end).startOf("day");

  while (!day.isAfter(last)) {
    dates.push(day.toDate());
    day = day.add(1, "day");
  }
  return dates;
};

// Energy in kWh. A reported `eu` wins; otherwise power (W) over the interval.
export const calculateUnits = (timeDiffMs, p, eu) => {
  const units =
    eu !== undefined && eu !== null && eu !== ""
      ? Number(eu)
      : (p * (timeDiffMs / 3_600_000)) / 1000;
  return Number.isNaN(units) ? 0 : units;
};

export const deviceLabel = (device) =>
  device.name ? `${device.name}-${device.deviceId}` : device.deviceId;

const formatDate = (value) => dayjs(value).format(DATE_FORMAT);
const formatTime = (value) => dayjs(value).format(TIME_FORMAT);
const fixed = (value, digits = 2) => Number(value ?? 0).toFixed(digits);
const round = (value, digits) => Number(Number(value ?? 0).toFixed(digits));

// /analysis/today for today, /analysis/day for any earlier date. assetId is the
// appliance's assetId.
export const fetchDay = (deviceId, date, assetId) =>
  dayjs(date).isSame(dayjs(), "day")
    ? fetchAnalysisToday(deviceId, assetId)
    : fetchAnalysisDay(deviceId, date, assetId);

// Column order for the CSV: every key that appears in any row, in the order it
// first appears, so a row missing a column does not drop it from the file.
export const csvHeaders = (rows) => {
  const seen = new Set();
  for (const row of rows) for (const key of Object.keys(row)) seen.add(key);
  return [...seen];
};

// One CSV line, every cell quoted the way react-csv did it.
const csvCell = (value) =>
  `"${value === undefined || value === null ? "" : String(value).replace(/"/g, '""')}"`;
const CSV_LINES_PER_PART = 5000;

// The CSV as Blob parts. Lines are joined a few thousand at a time rather than
// into one string: a store-wide report runs to hundreds of thousands of lines,
// which as a single string can exceed what the browser allows.
export const csvParts = (rows) => {
  const headers = csvHeaders(rows);
  // The BOM tells Excel the file is UTF-8.
  const parts = ["\uFEFF", headers.map(csvCell).join(",")];

  for (let i = 0; i < rows.length; i += CSV_LINES_PER_PART) {
    const lines = rows
      .slice(i, i + CSV_LINES_PER_PART)
      .map((row) => headers.map((key) => csvCell(row[key])).join(","));
    parts.push("\n", lines.join("\n"));
  }

  return parts;
};

// Builds the file only when it is asked for. CSVLink built it on every render,
// which froze the page once a report ran to a few hundred thousand rows.
export const downloadCsv = (rows, fileName) => {
  const url = URL.createObjectURL(new Blob(csvParts(rows), { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoked on the next tick so the browser has started the download first.
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

export const reportFileName = (report) =>
  `${formatDate(report.date.startDate)} - ${formatDate(report.date.endDate)}_${report.type}.csv`;

// Like Promise.all over items.map(task), but with at most `limit` tasks in
// flight, so a long list does not fire every request at once. Results keep the
// order of `items`.
export const mapWithConcurrency = async (items, limit, task) => {
  const results = new Array(items.length);
  let next = 0;

  const worker = async () => {
    while (next < items.length) {
      const index = next++;
      results[index] = await task(items[index], index);
    }
  };

  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return results;
};

const step = (reportId) => reduxStore.dispatch(incrementCall(reportId));

//------------------------------------ A. Daily units (kWh / kVAh) ----------------------

// Sum of unitsConsumed across the day's entries, plus any synced readings that
// are plausible (<= MAX_SYNCED_UNIT; anything larger is bad data).
export const sumDayUnits = (units) => {
  let total = 0;
  for (const entry of Object.values(units || {})) {
    total += Number(entry?.unitsConsumed) || 0;
    if (entry?.syncedData) {
      for (const value of Object.values(entry.syncedData)) {
        const reading = Number(value);
        if (!Number.isNaN(reading) && reading <= MAX_SYNCED_UNIT) total += reading;
      }
    }
  }
  return total;
};

// kVAh for the day: the sum of each slot's unitsVA. Slots without a usable
// reading count as 0. Offline and synced readings are left out.
export const sumDayKvah = (units) => {
  const entries = Object.values(units || {});
  let kvah = 0;
  for (const entry of entries) {
    const consumed = Number(entry?.unitsVA);
    if (consumed) kvah += consumed
  }
  return Number.isFinite(kvah) ? kvah : 0;
};

// kWh for the day as worked out from the load logs: the sum of each slot's
// unitsByLoadLogs. Slots without a usable reading count as 0.
export const sumDayLoadLogUnits = (units) => {
  let kwh = 0;
  for (const entry of Object.values(units || {})) {
    const consumed = Number(entry?.unitsByLoadLogs);
    if (consumed) kwh += consumed;
  }
  return Number.isFinite(kwh) ? kwh : 0;
};

// One device's units for one day from /units/day: kVAh by default, or kWh
// (consumed plus synced readings) with kvah = false. 0 when the call fails.
export const fetchDailyUnitsSnapshot = async (deviceId, date, kvah = true) => {
  try {
    const units = await fetchUnitsDay(String(deviceId).trim(), date);
    return kvah ? sumDayKvah(units) : sumDayUnits(units);
  } catch (error) {
    return 0;
  }
};

//------------------------------------ B. Temperature -------------------------------------

const temperatureRows = async (reportId, dates, devices) => {
  const rows = [];

  for (const date of dates) {
    for (const device of devices) {
      const snapshot = await fetchDay(device.deviceId, date, device.assetId);

      for (const log of snapshot?.temperatureLogs ?? []) {
        if (!(log?.t > 0)) continue;
        rows.push({
          Date: formatDate(date),
          Time: formatTime(log.ts),
          Site: device.houseName,
          "Machine name": deviceLabel(device),
          Temperature: fixed(log.t),
        });
      }

      step(reportId);
    }
  }

  return rows;
};

//------------------------------------ C. Equipment Logs ----------------------------------

// Phase groups keyed by `m`; a missing or zero `m` is phase 0. Integer-like
// keys iterate in ascending order, so phases come out 0, 1, 2...
export const groupByPhase = (logs) => {
  const groups = { 0: [] };
  for (const log of logs) {
    const phase = log.m === undefined || log.m === null || log.m === 0 ? 0 : log.m;
    (groups[phase] = groups[phase] || []).push(log);
  }
  return groups;
};

export const equipmentLogRows = (loadLogs, date, device) => {
  const rows = [];
  const sorted = [...(loadLogs ?? [])]
    .filter((log) => log.ct !== "connect" && log.ct !== "disconnect")
    .sort((a, b) => Number(a.ts) - Number(b.ts));

  const groups = groupByPhase(sorted);

  for (const [phase, logs] of Object.entries(groups)) {
    logs.forEach((log, index) => {
      if (log.ot !== undefined && log.ot !== null) return;

      const next = logs[index + 1];
      const units =
        log.eu !== undefined && log.eu !== null && log.eu !== ""
          ? Number(log.eu) || 0
          : next
            ? calculateUnits(next.ts - log.ts, log.p, log.eu)
            : 0;

      rows.push({
        Date: formatDate(date),
        Time: formatTime(log.ts),
        Site: device.houseName,
        "Machine name": deviceLabel(device),
        Phase: Number(phase) + 1,
        Voltage: fixed(log.v),
        PF: fixed(log.pf),
        Current: fixed(log.c),
        Power: fixed(log.p),
        "Units Consumed": fixed(units),
      });
    });
  }

  return rows;
};

// Collects into `rows` as it goes, so a failure partway keeps what was read.
const equipmentLogsRows = async (reportId, dates, devices, rows) => {
  for (const date of dates) {
    for (const device of devices) {
      const snapshot = await fetchDay(device.deviceId, date, device.assetId);
      rows.push(...equipmentLogRows(snapshot?.loadLogs, date, device));
      step(reportId);
    }
  }
  return rows;
};

//------------------------------------ D. Hourly Units Data -------------------------------

export const hourlyEntryRows = (entry, device) => {
  const breakdown = entry?.breakdown ?? {};
  const rows = [];

  for (let hour = 0; hour < 24; hour++) {
    const phase1 = Number(breakdown["0"]?.[hour]) || 0;
    const phase2 = Number(breakdown["1"]?.[hour]) || 0;
    const phase3 = Number(breakdown["2"]?.[hour]) || 0;

    rows.push({
      Day: formatDate(entry.day),
      Hour: hour,
      DeviceId: entry.deviceId,
      Name: device?.name || entry.deviceId,
      "Phase 1 Units": round(phase1, 4),
      "Phase 2 Units": round(phase2, 4),
      "Phase 3 Units": round(phase3, 4),
      "Total Units": round(phase1 + phase2 + phase3, 4),
    });
  }

  return rows;
};

const hourlyUnitsRows = async (reportId, dateRange, devices) => {
  const rows = [];
  const startDate = dayjs(dateRange.startDate).startOf("day").toDate().toISOString();
  const endDate = dayjs(dateRange.endDate).endOf("day").toDate().toISOString();
  const byId = new Map(devices.map((device) => [device.deviceId, device]));

  for (let i = 0; i < devices.length; i += HOURLY_BATCH_SIZE) {
    const batch = devices.slice(i, i + HOURLY_BATCH_SIZE).map((d) => d.deviceId);
    const hourlyData = await fetchHourlyMultiple(batch, startDate, endDate);

    for (const entry of hourlyData) {
      rows.push(...hourlyEntryRows(entry, byId.get(entry.deviceId)));
      // One step per device-day, which is what totalCalls counts.
      step(reportId);
    }
  }

  return rows;
};

//------------------------------------ E. Store Analytics ---------------------------------

// Laid out like the "JFL 15 min block Data - New Format" sheet: one row per
// device, day and fifteen minute bucket, with each metric as a column. The
// service sends one entry per device, day and metric, its buckets keyed "HH:mm".
// Every device gets every column, so meters and temperature sensors share one
// shape; a metric the device does not report is left blank.
export const STORE_METRICS = [
  ["unitsConsumed", "Units", (v) => round(v, 3)],
  ["onTime", "On Time", (v) => round(v, 2)],
  ["onlineTime", "Online Time", (v) => round(v, 2)],
  ["onlineLogs", "No of Online Logs", (v) => v],
  ["avgTemperature", "Avg Temp", (v) => fixed(v)],
];

// The columns every store analytics row starts with, ahead of the metrics.
export const STORE_ANALYTICS_COLUMNS = [
  "DPI ID",
  "Store Name",
  "Device ID",
  "Device Type",
  "Appliance Name",
  "Date",
  "Time",
];

// "HH:mm" for each bucket of the day; the API key and the sheet's Time cell.
const STORE_SLOTS = Array.from({ length: 96 }, (_, i) =>
  dayjs().startOf("day").add(i * 15, "minute").format("HH:mm")
);

// `analytics` is the data object of GET /v1/stores/:code/analytics. Days the
// service has not processed, or failed to, still get their rows, with the
// metrics left blank. `metrics` limits the metric columns to those keys, in
// STORE_METRICS order; left out, every metric is written.
export const storeAnalyticsRows = (analytics, property = {}, metrics) => {
  const columns = metrics
    ? STORE_METRICS.filter(([metric]) => metrics.includes(metric))
    : STORE_METRICS;
  const storeCode = analytics?.storeCode || property.houseId || "";
  const storeName = analytics?.storeName || property.houseName || "";

  // deviceId -> { device, days: date -> { metric: entry } }, in the order the
  // service lists devices.
  const devices = new Map();
  for (const entry of analytics?.telemetry ?? []) {
    if (!entry?.deviceId || !entry.date) continue;
    if (!devices.has(entry.deviceId)) devices.set(entry.deviceId, { device: entry, days: new Map() });
    const { days } = devices.get(entry.deviceId);
    if (!days.has(entry.date)) days.set(entry.date, {});
    if (entry.status === "complete" && entry.metric) days.get(entry.date)[entry.metric] = entry;
  }

  const rows = [];

  for (const { device, days } of devices.values()) {
    [...days.keys()].sort().forEach((date) => {
      const metrics = days.get(date);
      const day = dayjs(date).format("D MMM");

      for (const slot of STORE_SLOTS) {
        const row = {
          "DPI ID": storeCode,
          "Store Name": storeName,
          "Device ID": device.deviceId,
          // The asset's category, as Device Master sets it.
          "Device Type": String(device.category ?? device.deviceType ?? "").trim(),
          "Appliance Name": device.deviceName || "",
          Date: day,
          Time: slot,
        };

        for (const [metric, column, format] of columns) {
          const value = metrics[metric]?.[slot];
          row[column] = value === undefined || value === null ? "" : format(value);
        }

        rows.push(row);
      }
    });
  }

  return rows;
};

// One call per store covers the whole range. Stores are fetched
// STORE_ANALYTICS_CONCURRENCY at a time; the rows still come out in the order
// the stores were picked.
const storeAnalyticsReport = async (reportId, dateRange, properties) => {
  const startDate = dayjs(dateRange.startDate).format("YYYY-MM-DD");
  const endDate = dayjs(dateRange.endDate).format("YYYY-MM-DD");

  const perStore = await mapWithConcurrency(
    properties,
    STORE_ANALYTICS_CONCURRENCY,
    async (property) => {
      const analytics = await fetchStoreAnalytics(property.houseId, startDate, endDate);
      step(reportId);
      return storeAnalyticsRows(analytics, property);
    }
  );

  // A loop rather than push(...rows): a store's rows can run into thousands,
  // past what spreading into arguments allows across many stores.
  const rows = [];
  for (const storeRows of perStore) for (const row of storeRows) rows.push(row);
  return rows;
};

//------------------------------------ F. Unit Consumed - Day Wise (kVAh) ---------------

// One row per device per day, devices in the order picked and each device's
// days in order. Devices typed in by hand have no name or property.
const unitDayWiseRows = async (reportId, dates, devices) => {
  const rows = [];

  for (const device of devices) {
    for (const date of dates) {
      // One call gives both: kVAh from unitsVA, KWH from the load logs.
      const units = await fetchUnitsDay(String(device.deviceId).trim(), date, device.assetId).catch(() => ({}));
      rows.push({
        "Device Id": device.deviceId,
        "Appliance Name": device.name || "",
        Date: formatDate(date),
        Site: device.houseName || NO_PROPERTY_SITE,
        "kVAh": round(sumDayKvah(units), 2),
        KWH: round(sumDayLoadLogUnits(units), 2),
      });
      step(reportId);
    }
  }

  return rows;
};

//------------------------------------ Runner ---------------------------------------------

// Starts a report in the background and returns at once. The entry is created
// before the first call so the table shows the row immediately; progress and
// the result land on it through the store.
//
// `properties` is [{ houseId, houseName }] for the selected properties;
// `devices` is the flat device list for those properties, or the picked
// equipment for the reports that take one.
export const runReport = async (reportId, { type, dateRange, devices, properties }) => {
  const dates = findDatesInRange(dateRange.startDate, dateRange.endDate);
  const dispatch = reduxStore.dispatch;

  dispatch(
    addReport(reportId, {
      date: { startDate: dateRange.startDate, endDate: dateRange.endDate },
      type,
      data: [],
      totalCalls: Math.max(
        type === REPORT_TYPES.storeAnalytics
          ? properties.length
          : dates.length * devices.length,
        1
      ),
      currentCall: 0,
      success: undefined,
    })
  );

  // Equipment Logs fills this in place so its partial rows survive an error.
  const partial = [];

  try {
    let rows = [];
    if (type === REPORT_TYPES.unitDayWise) {
      rows = await unitDayWiseRows(reportId, dates, devices);
    } else if (type === REPORT_TYPES.temperature) {
      rows = await temperatureRows(reportId, dates, devices);
    } else if (type === REPORT_TYPES.equipmentLogs) {
      rows = await equipmentLogsRows(reportId, dates, devices, partial);
    } else if (type === REPORT_TYPES.hourlyUnits) {
      rows = await hourlyUnitsRows(reportId, dateRange, devices);
    } else if (type === REPORT_TYPES.storeAnalytics) {
      rows = await storeAnalyticsReport(reportId, dateRange, properties);
    }

    // Read the total back rather than trusting the count: the hourly service
    // can return fewer device-days than were asked for, and a finished report
    // should not sit at 80%.
    const { totalCalls } = reduxStore.getState().unitReport.reports[reportId];
    dispatch(addReport(reportId, { data: rows, success: true, currentCall: totalCalls }));
  } catch (error) {
    console.error(`${type} report failed`, error);
    dispatch(addReport(reportId, { data: partial, success: false }));
  }
};
